import { useState, useEffect } from 'react';

import { useIntl } from 'react-intl';
import Notification from '@rio-cloud/rio-uikit/Notification';
import AegRequestFormBody from './AegRequestFormBody';
import AegRequestFormFooter from './AegRequestFormFooter';
import { INITIAL_AEG_REQUEST_FORM } from './newAegRequest/constants';
import { getRequiredFields, validateRequiredFields } from '././newAegRequest/requiredFields';
import useAegVehicle from './useAegVehicle';
import { getVehicleFormValues } from '././newAegRequest/vehicleFormValues';
import { AegRequestErrors, AegRequestField, AegRequestForm } from '../../../../models/newAEG';
import { buildAegRequestPayload } from '../../../../utils/aeg';
import { createAegOccurrence, uploadAegFile } from '../../../../api/aegOccurrences';

import './styles.css';

type RequestDetailProps = {
    onClose: () => void;
    onCreated?: () => void;
};

export default function NewAEG({ onClose, onCreated }: RequestDetailProps) {
    const intl = useIntl();
    const [values, setValues] = useState<AegRequestForm>(INITIAL_AEG_REQUEST_FORM);
    const [errors, setErrors] = useState<AegRequestErrors>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const requiredFields = getRequiredFields(values);
    const { vehicle, isLoadingVehicle, fetchVehicle, resetVehicle } = useAegVehicle();

    const handleChange = <K extends AegRequestField>(field: K, value: AegRequestForm[K]) => {
        setValues(previous => ({ ...previous, [field]: value }));
        setErrors(previous => (field === 'moveTo' ? {} : { ...previous, [field]: false }));
    };

    const loadVehicle = async (chassis: string) => {
        const loadedVehicle = await fetchVehicle(chassis);
        if (!loadedVehicle) {
            return;
        }

        const vehicleValues = getVehicleFormValues(loadedVehicle);
        const filledFields = Object.keys(vehicleValues) as AegRequestField[];

        setValues(previous => ({ ...previous, ...vehicleValues }));
        setErrors(previous => ({
            ...previous,
            ...Object.fromEntries(filledFields.map(field => [field, false])),
        }));
    };

    useEffect(() => {
        if (values.chassis.length === 17) {
            loadVehicle(values.chassis);
            return;
        }

        resetVehicle();
    }, [values.chassis]);

    const handleSubmit = async () => {
        const newErrors = validateRequiredFields(values, requiredFields);
        setErrors(newErrors);
        if (Object.values(newErrors).some(Boolean)) {
            return;
        }

        setIsSubmitting(true);

        const createdAeg = await createAegOccurrence(buildAegRequestPayload(values));

        if (!createdAeg) {
            Notification.error(intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.create.error' }));
            setIsSubmitting(false);
            return;
        }

        onCreated?.();

        const failedUploadFileNames: string[] = [];

        for (const file of values.attachments) {
            const uploaded = await uploadAegFile(createdAeg.id, file);
            if (!uploaded) {
                failedUploadFileNames.push(file.name);
            }
        }

        if (failedUploadFileNames.length) {
            Notification.error(
                intl.formatMessage(
                    { id: 'intl-msg:support.aeg.newRequest.create.uploadErrorAfterCreate' },
                    { fileNames: failedUploadFileNames.join(', ') }
                )
            );
        } else {
            Notification.success(intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.create.success' }));
        }

        setIsSubmitting(false);
        onClose();
    };

    return (
        <>
            <div className='aeg-request-detail-scrim bg-black opacity-50' onClick={onClose} />
            <dialog
                aria-modal='true'
                className='aeg-request-detail-modal aeg-new-request-modal display-flex flex-column bg-white text-size-14'
                open
            >
                <header className='display-flex align-items-center gap-10 padding-15 border border-bottom-only border-left-none border-right-none border-top-none'>
                    <div className='flex-1-1'>
                        <div>
                            <h1 className='text-color-darkest text-size-h4 margin-top-0 margin-bottom-5'>
                                {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.title' })}
                            </h1>
                            <p className='text-size-12 text-color-gray margin-0'>
                                {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.requiredHint' })}
                            </p>
                        </div>
                    </div>
                    <button aria-label={intl.formatMessage({ id: 'intl-msg:support.aeg.common.close' })} className='btn btn-link padding-0' onClick={onClose} type='button'>
                        <span aria-hidden='true' className='aeg-request-detail-close-icon rioglyph rioglyph-remove' />
                    </button>
                </header>
                <div className='overflow-y-auto pb-6 px-4'>
                    <main className='padding-top-10 padding-bottom-20'>
                        <AegRequestFormBody
                            values={values}
                            errors={errors}
                            requiredFields={requiredFields}
                            onChange={handleChange}
                            vehicle={vehicle}
                            isLoadingVehicle={isLoadingVehicle}
                        />
                    </main>
                    <footer className='padding-top-15'>
                        <AegRequestFormFooter onSubmit={handleSubmit} isSubmitting={isSubmitting} />
                    </footer>
                </div>
            </dialog>
        </>
    );
}
